import React, { useEffect, useId, useState } from 'react';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { Modal, Input, Select, Checkbox } from '../ui';
import PhoneInput from '../ui/PhoneInput';
import { PROGRAM_BRAND_ACCENT } from '../../utils/programLabels';
import { phoneDigitsOnly } from '../../utils/phoneFormat';

export interface ContactFormData {
  civility: 'Monsieur' | 'Madame';
  purchaseReason: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  rgpd: boolean;
}

type ContactFormVariant = 'full' | 'lead';

interface ContactFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ContactFormData) => void | Promise<void>;
  title?: string;
  description?: string;
  primaryColor?: string;
  /** lead : prénom, nom, email, téléphone (+ RGPD) — pour téléchargements. */
  variant?: ContactFormVariant;
  submitLabel?: string;
  successTitle?: string;
  successMessage?: string;
}

const DEFAULT_SUCCESS_TITLE = 'Demande envoyée';
const DEFAULT_SUCCESS_MESSAGE =
  'Votre demande a bien été envoyée. Vous allez recevoir des éléments supplémentaires par e-mail.';

const ContactFormModal: React.FC<ContactFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  title = 'Vos informations',
  description = 'Merci de remplir ce formulaire pour accéder au contenu.',
  primaryColor = PROGRAM_BRAND_ACCENT,
  variant = 'full',
  submitLabel,
  successTitle = DEFAULT_SUCCESS_TITLE,
  successMessage = DEFAULT_SUCCESS_MESSAGE,
}) => {
  const isLead = variant === 'lead';
  const rgpdCheckboxId = useId();
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState<ContactFormData>({
    civility: 'Monsieur',
    purchaseReason: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    rgpd: false,
  });

  const [errors, setErrors] = useState<Partial<Record<keyof ContactFormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setShowSuccess(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    setShowSuccess(false);
    onClose();
  };

  const civilityOptions = [
    { value: 'Monsieur', label: 'Monsieur' },
    { value: 'Madame', label: 'Madame' },
  ];

  const purchaseReasonOptions = [
    { value: 'residence-principale', label: 'Résidence principale' },
    { value: 'residence-secondaire', label: 'Résidence secondaire' },
    { value: 'investissement-locatif', label: 'Investissement locatif' },
    { value: 'autre', label: 'Autre' },
  ];

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof ContactFormData, string>> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Le prénom est requis';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Le nom est requis';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Format d\'email invalide';
    }

    const phoneDigits = phoneDigitsOnly(formData.phone);
    if (!phoneDigits) {
      newErrors.phone = 'Le téléphone est requis';
    } else if (phoneDigits.length < 10) {
      newErrors.phone = 'Numéro incomplet (10 chiffres)';
    } else if (!/^0[1-9]\d{8}$/.test(phoneDigits)) {
      newErrors.phone = 'Format de téléphone invalide';
    }

    if (!isLead && !formData.purchaseReason) {
      newErrors.purchaseReason = 'Veuillez sélectionner une option';
    }

    if (!formData.rgpd) {
      newErrors.rgpd = 'Vous devez accepter les conditions RGPD';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit(formData);

      setFormData({
        civility: 'Monsieur',
        purchaseReason: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        rgpd: false,
      });
      setErrors({});
      setShowSuccess(true);
    } catch (error) {
      console.error('Erreur lors de l\'envoi du formulaire:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof ContactFormData, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    
    // Effacer l'erreur du champ modifié
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      className="max-w-lg"
    >
      {showSuccess ? (
        <div className="space-y-5 py-2 text-center sm:py-4">
          <div
            className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50"
            aria-hidden
          >
            <CheckCircleIcon className="!h-9 !w-9 text-emerald-600" />
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-neutral-900">{successTitle}</h2>
            <p className="text-sm leading-relaxed text-neutral-600">{successMessage}</p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="w-full rounded-lg px-4 py-3 font-medium text-white transition-opacity hover:opacity-90"
            style={{ backgroundColor: primaryColor }}
          >
            Fermer
          </button>
        </div>
      ) : (
      <div className="space-y-4">
        {/* Header */}
        <div>
          <h2 className="text-xl font-semibold text-neutral-900 mb-1.5">{title}</h2>
          {description && (
            <p className="text-neutral-600 text-sm leading-snug">{description}</p>
          )}
        </div>

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLead ? (
            <div>
              <Select
                label="Civilité"
                options={civilityOptions}
                value={formData.civility}
                onChange={(value) => handleChange('civility', value)}
                required
              />
            </div>
          ) : null}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Input
                label="Prénom"
                type="text"
                value={formData.firstName}
                onChange={(value) => handleChange('firstName', value)}
                placeholder="Votre prénom"
                required
                error={errors.firstName}
              />
            </div>
            <div>
              <Input
                label="Nom"
                type="text"
                value={formData.lastName}
                onChange={(value) => handleChange('lastName', value)}
                placeholder="Votre nom"
                required
                error={errors.lastName}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Input
                label="Email"
                type="email"
                value={formData.email}
                onChange={(value) => handleChange('email', value)}
                placeholder="votre@email.com"
                required
                error={errors.email}
              />
            </div>
            <div>
              <PhoneInput
                label="Téléphone"
                value={formData.phone}
                onChange={(value) => handleChange('phone', value)}
                required
                error={errors.phone}
              />
            </div>
          </div>

          {!isLead ? (
            <div>
              <Select
                label="Je souhaite acheter pour"
                options={purchaseReasonOptions}
                value={formData.purchaseReason}
                onChange={(value) => handleChange('purchaseReason', value)}
                placeholder="Sélectionnez une option"
                required
                error={errors.purchaseReason}
              />
            </div>
          ) : null}

          {/* RGPD */}
          <div>
            <div className="flex items-start">
              <div className="flex-shrink-0 pt-1.5">
                <Checkbox
                  id={rgpdCheckboxId}
                  checked={formData.rgpd}
                  onChange={(checked) => handleChange('rgpd', checked)}
                  required
                  error={errors.rgpd}
                />
              </div>
              <label
                htmlFor={rgpdCheckboxId}
                className="text-sm font-medium text-neutral-700 cursor-pointer select-none ml-2"
              >
                <span>
                  J'accepte que mes données soient utilisées pour me recontacter dans le cadre de ma demande.{' '}
                  <a 
                    href="/rgpd" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:underline ml-1"
                    style={{ color: primaryColor }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    En savoir plus
                  </a>
                </span>
                {errors.rgpd && (
                  <span className="text-error-500 ml-1">*</span>
                )}
              </label>
            </div>
            {errors.rgpd && (
              <p className="text-error-600 text-sm mt-1 ml-7" role="alert">
                {errors.rgpd}
              </p>
            )}
          </div>

          {/* Boutons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-3 border border-neutral-300 rounded-lg text-neutral-700 font-medium hover:bg-neutral-50 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-3 rounded-lg text-white font-medium transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: primaryColor }}
            >
              {isSubmitting
                ? 'Envoi en cours...'
                : submitLabel || (isLead ? 'Télécharger' : 'Envoyer')}
            </button>
          </div>
        </form>
      </div>
      )}
    </Modal>
  );
};

export default ContactFormModal;

